import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiBell, FiChevronDown } from 'react-icons/fi';
import {
  TopNavbar,
  NAV_ICON_SEARCH_INDEX,
  NAV_ICON_FAVORITES_INDEX,
  NAV_ICON_SETTINGS_INDEX,
  NAV_ICON_LOCK_INDEX,
  NAV_ICON_HISTORY_INDEX,
} from '../../components/TopNavbar/TopNavbar';
import { ContentNav } from '../../components/ContentNav/ContentNav';
import { useParentalControls } from '../../contexts/ParentalControlsContext';
import { useLockToggle } from '../../hooks/useLockToggle';
import { useContentNavIds } from '../../hooks/useContentNavIds';
import PasswordModal from '../../components/PasswordModal/PasswordModal';
import { useSchedules } from '../../hooks/useSchedules';
import type { ScheduledProgram } from '../../hooks/useSchedules';
import {
  fetchLiveEventsDetailed,
  fetchTodayEventsDetailed,
  type LiveEvent,
  type UpcomingEvent,
} from '../../api/sportsAPI';
import {
  SPORTS,
  getLeagueGroups,
  getRegionCode,
  getCountryNameForApi,
  type LeagueDef,
} from '../../data/sportsData';
import './Sports.css';

// ─── Types ───────────────────────────────────────────────────────────────────

type MatchRow =
  | ({ kind: 'live' } & LiveEvent)
  | ({ kind: 'upcoming' } & UpcomingEvent);

type FocusSection =
  | 'topnav'
  | 'contentnav'
  | 'filters'
  | 'comp-dropdown'
  | 'matches';

type FilterCol = 'sport' | 'comp';

const NAV_ICON_COUNT = 5;

interface CompItem {
  isHeader: boolean;
  headerLabel?: string;
  league: LeagueDef | null; // null = "All competitions"
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface SportsProps {
  isActive?: boolean;
  onBack?: () => void;
  onNavigate?: (sectionId: string) => void;
  onOpenSettings?: () => void;
  onOpenSearch?: () => void;
  onOpenFavorites?: () => void;
  onOpenHistory?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const Sports: React.FC<SportsProps> = ({
  isActive = true,
  onBack,
  onNavigate,
  onOpenSettings,
  onOpenSearch,
  onOpenFavorites,
  onOpenHistory,
}) => {
  const { t, i18n } = useTranslation();
  const { isBlocked } = useParentalControls();
  const {
    handleLockIconClick,
    handleLockPasswordConfirm,
    handleLockPasswordCancel,
    lockModalOpen,
    lockModalMode,
    lockModalError,
  } = useLockToggle();
  const contentNavIds = useContentNavIds();

  // ─── Sport / filter state ─────────────────────────────────────────────
  const [selectedSportIdx, setSelectedSportIdx] = useState(0);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null);

  // ─── Dropdown open/nav state ──────────────────────────────────────────
  const [compDropdownOpen, setCompDropdownOpen] = useState(false);
  const [compDropdownIdx, setCompDropdownIdx] = useState(0);
  // ─── Focus state ──────────────────────────────────────────────────────
  const [focusSection, setFocusSection] = useState<FocusSection>('matches');
  const [filterCol, setFilterCol] = useState<FilterCol>('sport');
  const [sportPillFocusIdx, setSportPillFocusIdx] = useState(0);
  const [topnavFocusedIdx, setTopnavFocusedIdx] = useState(0);
  const [contentnavFocusedId, setContentnavFocusedId] = useState('games');
  const [matchFocusedIdx, setMatchFocusedIdx] = useState(0);
  const [bellFocused, setBellFocused] = useState(false);

  // ─── Events ───────────────────────────────────────────────────────────
  const [liveEvents, setLiveEvents] = useState<LiveEvent[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [sportsApiHint, setSportsApiHint] = useState<string | null>(null);

  const matchesContainerRef = useRef<HTMLDivElement>(null);
  const compListRef = useRef<HTMLDivElement>(null);

  const { toggleSchedule, isScheduled } = useSchedules();

  // ─── Static data ──────────────────────────────────────────────────────
  const regionCode = useMemo(function() { return getRegionCode(); }, []);
  const todayStr    = useMemo(function() {
    return new Date().toLocaleDateString('en-CA');
  }, []);
  const tomorrowStr = useMemo(function() {
    return new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleDateString('en-CA');
  }, []);
  const selectedSport = SPORTS[selectedSportIdx];

  const leagueGroups = useMemo(
    function() { return getLeagueGroups(selectedSport.id, regionCode); },
    [selectedSport.id, regionCode]
  );

  // Build competition dropdown items (with section headers)
  const compItems: CompItem[] = useMemo(function() {
    var items: CompItem[] = [];
    items.push({ isHeader: false, league: null }); // "All competitions"

    var regional = leagueGroups.regional;
    var continental = leagueGroups.continental;
    var world = leagueGroups.world;
    var top = leagueGroups.top;

    if (regional.length > 0) {
      items.push({ isHeader: true, headerLabel: t('sports.sections.regional') + ' (' + regionCode + ')', league: null });
      regional.forEach(function(l) { items.push({ isHeader: false, league: l }); });
    }
    if (continental.length > 0) {
      items.push({ isHeader: true, headerLabel: t('sports.sections.continental'), league: null });
      continental.forEach(function(l) { items.push({ isHeader: false, league: l }); });
    }
    if (world.length > 0) {
      items.push({ isHeader: true, headerLabel: t('sports.sections.world'), league: null });
      world.forEach(function(l) { items.push({ isHeader: false, league: l }); });
    }
    if (top.length > 0) {
      items.push({ isHeader: true, headerLabel: t('sports.sections.topLeagues'), league: null });
      top.forEach(function(l) { items.push({ isHeader: false, league: l }); });
    }
    return items;
  }, [leagueGroups, t, regionCode]);

  // Selectable items only (no headers)
  const compSelectableItems = useMemo(
    function() { return compItems.filter(function(i) { return !i.isHeader; }); },
    [compItems]
  );

  // ─── Filtered rows ────────────────────────────────────────────────────
  const rows: MatchRow[] = useMemo(function() {
    // Backend already returns only events relevant to the user's country — no client-side league filter
    var live: LiveEvent[] = liveEvents.slice();
    var upcoming: UpcomingEvent[] = upcomingEvents.slice();
    if (selectedLeagueId) {
      live = live.filter(function(e) { return e.idLeague === selectedLeagueId; });
      upcoming = upcoming.filter(function(e) { return e.idLeague === selectedLeagueId; });
    }
    var result: MatchRow[] = [];
    live.forEach(function(e)     { result.push(Object.assign({ kind: 'live' as const }, e)); });
    upcoming.forEach(function(e) { result.push(Object.assign({ kind: 'upcoming' as const }, e)); });
    return result;
  }, [liveEvents, upcomingEvents, selectedLeagueId]);

  const liveCount     = rows.filter(function(r) { return r.kind === 'live'; }).length;
  const upcomingCount = rows.filter(function(r) { return r.kind === 'upcoming'; }).length;

  // ─── Load events when sport changes ──────────────────────────────────
  useEffect(function() {
    setLoading(true);
    setSelectedLeagueId(null);
    setMatchFocusedIdx(0);
    setSportsApiHint(null);
    var sport = SPORTS[selectedSportIdx].theSportsDbSport;
    var countryName = getCountryNameForApi(regionCode);
    Promise.allSettled([
      fetchLiveEventsDetailed(sport, countryName || undefined),
      fetchTodayEventsDetailed(sport, countryName || undefined),
    ]).then(function(results) {
      var liveRes  = results[0];
      var todayRes = results[1];
      var liveData  = liveRes.status === 'fulfilled' ? liveRes.value : { events: [] as LiveEvent[] };
      var todayData = todayRes.status === 'fulfilled' ? todayRes.value : { events: [] as UpcomingEvent[] };
      setLiveEvents(liveData.events);
      setUpcomingEvents(todayData.events);
      var tier = liveData.apiTier || todayData.apiTier;
      var configured = liveData.configured || todayData.configured;
      if (tier === 'free' && !configured) {
        setSportsApiHint(t('sports.freeApiHint', 'Usando API gratuita. Configure a chave premium no admin para mais jogos e placares ao vivo.'));
      }
      setLoading(false);
    });
  }, [selectedSportIdx]);

  // Scroll focused comp item into view
  useEffect(function() {
    if (!compDropdownOpen || !compListRef.current) return;
    var el = compListRef.current.querySelector<HTMLElement>('[data-comp-idx="' + compDropdownIdx + '"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [compDropdownIdx, compDropdownOpen]);

  // ─── Helpers ─────────────────────────────────────────────────────────
  const makeSchedule = useCallback(function(row: MatchRow): ScheduledProgram | null {
    if (row.kind !== 'upcoming') return null;
    var ev = row as { kind: 'upcoming' } & UpcomingEvent;
    if (!ev.dateEvent || !ev.strTime) return null;
    var startMs = new Date(ev.dateEvent + 'T' + ev.strTime.slice(0, 5)).getTime();
    if (isNaN(startMs)) return null;
    var endMs = startMs + 2 * 60 * 60 * 1000;
    var title = [ev.strHomeTeam, ev.strAwayTeam].filter(Boolean).join(' vs ');
    return {
      streamId: 'sport_' + ev.idEvent,
      channelName: ev.strLeague || '',
      channelIcon: ev.strLeagueBadge,
      title: title,
      startMs: startMs,
      endMs: endMs,
      addedAt: Date.now(),
    };
  }, []);

  function openCompDropdown() {
    var curIdx = 0;
    for (var i = 0; i < compSelectableItems.length; i++) {
      var item = compSelectableItems[i];
      if (item.league ? item.league.id === selectedLeagueId : selectedLeagueId === null) {
        curIdx = i;
        break;
      }
    }
    setCompDropdownIdx(curIdx);
    setCompDropdownOpen(true);
    setFocusSection('comp-dropdown');
  }

  function closeCompDropdown() {
    setCompDropdownOpen(false);
    setFocusSection('filters');
    setFilterCol('comp');
  }

  function selectCompItem(idx: number) {
    var item = compSelectableItems[idx];
    if (!item) return;
    setSelectedLeagueId(item.league ? item.league.id : null);
    closeCompDropdown();
  }

  // ─── Global keyboard handler ─────────────────────────────────────────
  useEffect(function() {
    if (!isActive) return;

    function onKeyDown(e: KeyboardEvent) {
      // Escape / back
      if (e.key === 'Escape' || e.key === 'GoBack' || e.key === 'BrowserBack') {
        e.preventDefault();
        if (compDropdownOpen)  { closeCompDropdown();    return; }
        onBack && onBack();
        return;
      }

      // Competition dropdown navigation (no real input involved)
      if (focusSection === 'comp-dropdown') {
        switch (e.key) {
          case 'ArrowUp':
            e.preventDefault();
            setCompDropdownIdx(function(prev) { return Math.max(prev - 1, 0); });
            break;
          case 'ArrowDown':
            e.preventDefault();
            setCompDropdownIdx(function(prev) {
              return Math.min(prev + 1, compSelectableItems.length - 1);
            });
            break;
          case 'Enter':
          case ' ':
            e.preventDefault();
            selectCompItem(compDropdownIdx);
            break;
        }
        return;
      }

      // Main section navigation
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (focusSection === 'contentnav') {
            setFocusSection('topnav');
          } else if (focusSection === 'filters') {
            setFocusSection('contentnav');
          } else if (focusSection === 'matches') {
            if (matchFocusedIdx > 0) {
              setBellFocused(false);
              setMatchFocusedIdx(function(prev) { return prev - 1; });
            } else {
              setFocusSection('filters');
              setFilterCol('sport');
              setSportPillFocusIdx(selectedSportIdx);
            }
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setFocusSection('contentnav');
          } else if (focusSection === 'contentnav') {
            setFocusSection('filters');
            setSportPillFocusIdx(selectedSportIdx);
            setFilterCol('sport');
          } else if (focusSection === 'filters') {
            setFocusSection('matches');
            setMatchFocusedIdx(0);
            setBellFocused(false);
          } else if (focusSection === 'matches') {
            if (matchFocusedIdx < rows.length - 1) {
              setBellFocused(false);
              setMatchFocusedIdx(function(prev) { return prev + 1; });
            }
          }
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setTopnavFocusedIdx(function(prev) { return Math.max(0, prev - 1); });
          } else if (focusSection === 'contentnav') {
            var lIdx = contentNavIds.indexOf(contentnavFocusedId);
            if (lIdx > 0) setContentnavFocusedId(contentNavIds[lIdx - 1]);
          } else if (focusSection === 'filters') {
            if (filterCol === 'sport') {
              if (sportPillFocusIdx > 0) {
                setSportPillFocusIdx(function(prev) { return prev - 1; });
              }
            } else if (filterCol === 'comp') {
              setFilterCol('sport');
              setSportPillFocusIdx(SPORTS.length - 1);
            }
          } else if (focusSection === 'matches') {
            if (bellFocused) setBellFocused(false);
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setTopnavFocusedIdx(function(prev) { return Math.min(NAV_ICON_COUNT - 1, prev + 1); });
          } else if (focusSection === 'contentnav') {
            var rIdx = contentNavIds.indexOf(contentnavFocusedId);
            if (rIdx < contentNavIds.length - 1) setContentnavFocusedId(contentNavIds[rIdx + 1]);
          } else if (focusSection === 'filters') {
            if (filterCol === 'sport') {
              if (sportPillFocusIdx < SPORTS.length - 1) {
                setSportPillFocusIdx(function(prev) { return prev + 1; });
              } else {
                setFilterCol('comp');
              }
            }
          } else if (focusSection === 'matches') {
            var row = rows[matchFocusedIdx];
            if (row && row.kind === 'upcoming' && !bellFocused) setBellFocused(true);
          }
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusSection === 'topnav') {
            if (topnavFocusedIdx === NAV_ICON_SEARCH_INDEX)    onOpenSearch && onOpenSearch();
            if (topnavFocusedIdx === NAV_ICON_FAVORITES_INDEX) onOpenFavorites && onOpenFavorites();
            if (topnavFocusedIdx === NAV_ICON_SETTINGS_INDEX)  onOpenSettings && onOpenSettings();
            if (topnavFocusedIdx === NAV_ICON_LOCK_INDEX)      handleLockIconClick();
            if (topnavFocusedIdx === NAV_ICON_HISTORY_INDEX)   onOpenHistory && onOpenHistory();
          } else if (focusSection === 'contentnav') {
            if (contentnavFocusedId === 'games') {
              setFocusSection('matches');
            } else {
              onNavigate && onNavigate(contentnavFocusedId);
            }
          } else if (focusSection === 'filters') {
            if (filterCol === 'sport') {
              setSelectedSportIdx(sportPillFocusIdx);
            } else if (filterCol === 'comp') {
              openCompDropdown();
            }
          } else if (focusSection === 'matches' && bellFocused) {
            var bellRow = rows[matchFocusedIdx];
            if (bellRow && bellRow.kind === 'upcoming') {
              var sched = makeSchedule(bellRow);
              if (sched) toggleSchedule(sched);
            }
          }
          break;

        default:
          break;
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return function() { window.removeEventListener('keydown', onKeyDown); };
  }, [
    isActive, focusSection, filterCol, sportPillFocusIdx, selectedSportIdx,
    topnavFocusedIdx, contentnavFocusedId, contentNavIds,
    matchFocusedIdx, bellFocused, rows, makeSchedule, toggleSchedule,
    compDropdownOpen, compDropdownIdx, compSelectableItems,
    onBack, onNavigate, onOpenSearch, onOpenFavorites, onOpenSettings, onOpenHistory,
    handleLockIconClick,
  ]);

  // ─── Scroll focused match into view ──────────────────────────────────
  useEffect(function() {
    var container = matchesContainerRef.current;
    if (!container) return;
    var el = container.querySelector<HTMLElement>('[data-idx="' + matchFocusedIdx + '"]');
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [matchFocusedIdx]);

  // ─── Derived display values ───────────────────────────────────────────
  var locale = i18n.language || 'en';

  function selectedLeagueName(): string {
    if (!selectedLeagueId) return t('sports.allCompetitions');
    for (var i = 0; i < compSelectableItems.length; i++) {
      var item = compSelectableItems[i];
      if (item.league && item.league.id === selectedLeagueId) {
        return t(item.league.nameKey, { defaultValue: item.league.nameFallback });
      }
    }
    return t('sports.allCompetitions');
  }

  // ─── Blocked ──────────────────────────────────────────────────────────
  if (isBlocked('sports')) {
    return (
      <div className="sports sports--blocked">
        <TopNavbar
          focusedIndex={focusSection === 'topnav' ? topnavFocusedIdx : undefined}
          onIconClick={function(i) {
            if (i === NAV_ICON_SEARCH_INDEX)    onOpenSearch && onOpenSearch();
            if (i === NAV_ICON_FAVORITES_INDEX) onOpenFavorites && onOpenFavorites();
            if (i === NAV_ICON_SETTINGS_INDEX)  onOpenSettings && onOpenSettings();
            if (i === NAV_ICON_LOCK_INDEX)      handleLockIconClick();
            if (i === NAV_ICON_HISTORY_INDEX)   onOpenHistory && onOpenHistory();
          }}
        />
        <div className="sports__blocked-msg">
          <p>{t('parental.blockedMessage')}</p>
        </div>
        {lockModalOpen && (
          <PasswordModal
            mode={lockModalMode}
            error={lockModalError ? t(lockModalError) : undefined}
            onConfirm={handleLockPasswordConfirm}
            onCancel={handleLockPasswordCancel}
          />
        )}
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <>
      <div className="sports">
        <TopNavbar
          focusedIndex={focusSection === 'topnav' ? topnavFocusedIdx : undefined}
          onIconClick={function(i) {
            if (i === NAV_ICON_SEARCH_INDEX)    onOpenSearch && onOpenSearch();
            if (i === NAV_ICON_FAVORITES_INDEX) onOpenFavorites && onOpenFavorites();
            if (i === NAV_ICON_SETTINGS_INDEX)  onOpenSettings && onOpenSettings();
            if (i === NAV_ICON_LOCK_INDEX)      handleLockIconClick();
            if (i === NAV_ICON_HISTORY_INDEX)   onOpenHistory && onOpenHistory();
          }}
        />
        <ContentNav
          activeId="games"
          focusedId={focusSection === 'contentnav' ? contentnavFocusedId : undefined}
          onSelect={function(id) { onNavigate && onNavigate(id); }}
        />

        <div className="sports__body-area">

          {/* ── Filter bar ──────────────────────────────────────────── */}
          <div className="sports__filters">

            {/* Sport pills */}
            <div className="sports__sport-pills">
              {SPORTS.map(function(sport, idx) {
                var isSelected = idx === selectedSportIdx;
                var isFocused  = focusSection === 'filters' && filterCol === 'sport' && sportPillFocusIdx === idx;
                var cls = ['sports__sport-pill'];
                if (isSelected) cls.push('sports__sport-pill--active');
                if (isFocused)  cls.push('sports__sport-pill--focused');
                return (
                  <button
                    key={sport.id}
                    className={cls.join(' ')}
                    onClick={function() {
                      setFocusSection('filters');
                      setFilterCol('sport');
                      setSportPillFocusIdx(idx);
                      setSelectedSportIdx(idx);
                    }}
                  >
                    <span className="sports__sport-pill-icon">{sport.icon}</span>
                    {t(sport.nameKey)}
                  </button>
                );
              })}
            </div>

            <div className="sports__filter-sep" />

            {/* Competition selector */}
            <div className="sports__comp-wrap" style={{ position: 'relative' }}>
              <button
                className={[
                  'sports__comp-btn',
                  (focusSection === 'filters' && filterCol === 'comp') || compDropdownOpen
                    ? 'sports__comp-btn--focused'
                    : '',
                ].filter(Boolean).join(' ')}
                onClick={function() {
                  setFocusSection('filters');
                  setFilterCol('comp');
                  openCompDropdown();
                }}
              >
                <span className="sports__comp-btn-label">{selectedLeagueName()}</span>
                <FiChevronDown className="sports__comp-btn-arrow" />
              </button>

              {compDropdownOpen && (
                <div className="sports__comp-dropdown" ref={compListRef}>
                  {(function() {
                    var selectableIdx = -1;
                    return compItems.map(function(item, rawIdx) {
                      if (item.isHeader) {
                        return (
                          <div key={'hdr-' + rawIdx} className="sports__comp-dropdown-header">
                            {item.headerLabel}
                          </div>
                        );
                      }
                      selectableIdx++;
                      var sIdx = selectableIdx;
                      var isFocused = sIdx === compDropdownIdx;
                      var isChosen  = item.league
                        ? item.league.id === selectedLeagueId
                        : selectedLeagueId === null;
                      return (
                        <div
                          key={item.league ? item.league.id : 'all'}
                          data-comp-idx={sIdx}
                          className={[
                            'sports__comp-dropdown-item',
                            isFocused ? 'sports__comp-dropdown-item--focused' : '',
                            isChosen  ? 'sports__comp-dropdown-item--chosen'  : '',
                          ].filter(Boolean).join(' ')}
                          onClick={function() { selectCompItem(sIdx); }}
                        >
                          {item.league
                            ? t(item.league.nameKey, { defaultValue: item.league.nameFallback })
                            : t('sports.allCompetitions')}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>


          </div>{/* end filters */}

          {/* ── Matches ─────────────────────────────────────────────── */}
          <div className="sports__content">
            <div className="sports__matches" ref={matchesContainerRef}>
              {loading && (
                <div className="sports__loading">{t('common.loading')}</div>
              )}
              {!loading && rows.length === 0 && (
                <div className="sports__empty">
                  <div>{t('sports.noEvents')}</div>
                  {sportsApiHint && <div className="sports__empty-hint">{sportsApiHint}</div>}
                </div>
              )}

              {!loading && liveCount > 0 && (
                <div className="sports__section-header sports__section-header--live">
                  <span className="sports__section-dot" />
                  {t('sports.ongoing')} · {liveCount}
                </div>
              )}

              {!loading && rows.map(function(row, idx) {
                var prevRow = idx > 0 ? rows[idx - 1] : null;
                var needsUpcomingHeader = upcomingCount > 0
                  && row.kind === 'upcoming'
                  && (!prevRow || prevRow.kind === 'live');

                // Date label shown inside each upcoming card
                var rowDate = row.kind === 'upcoming' ? (row as UpcomingEvent).dateEvent : null;
                var dateLabelUpcoming = '';
                if (rowDate) {
                  if (rowDate === todayStr)         dateLabelUpcoming = t('sports.dateToday', 'Hoje');
                  else if (rowDate === tomorrowStr) dateLabelUpcoming = t('sports.dateTomorrow', 'Amanhã');
                  else {
                    try {
                      var dp = rowDate.split('-');
                      dateLabelUpcoming = dp[2] + '/' + dp[1];
                    } catch (_) { dateLabelUpcoming = rowDate; }
                  }
                }

                var isFocused    = focusSection === 'matches' && matchFocusedIdx === idx;
                var isBellFocused = isFocused && bellFocused;
                var isLive       = row.kind === 'live';
                var scheduleKey  = 'sport_' + row.idEvent;
                var scheduleTime = (function() {
                  if (row.kind !== 'upcoming') return 0;
                  var ev = row as UpcomingEvent;
                  if (!ev.dateEvent || !ev.strTime) return 0;
                  return new Date(ev.dateEvent + 'T' + ev.strTime.slice(0, 5)).getTime();
                })();
                var hasBell = row.kind === 'upcoming' && isScheduled(scheduleKey, scheduleTime);

                return (
                  <React.Fragment key={row.idEvent || idx}>
                    {needsUpcomingHeader && (
                      <div className="sports__section-header sports__section-header--upcoming">
                        <span className="sports__section-dot sports__section-dot--upcoming" />
                        {t('sports.upcoming')} · {upcomingCount}
                      </div>
                    )}
                    <div
                      data-idx={idx}
                      className={['match-row', isFocused ? 'match-row--focused' : ''].filter(Boolean).join(' ')}
                      onClick={function() {
                        setFocusSection('matches');
                        setMatchFocusedIdx(idx);
                      }}
                    >
                      {/* League */}
                      <div className="match-row__league">
                        {row.strLeagueBadge ? (
                          <img className="match-row__league-logo" loading="lazy" src={row.strLeagueBadge} alt={row.strLeague || ''} />
                        ) : (
                          <div className="match-row__league-logo match-row__league-logo--emoji">🏆</div>
                        )}
                        <div className="match-row__league-name">{row.strLeague}</div>
                      </div>

                      {/* Teams + center */}
                      <div className="match-row__teams">
                        <div className="match-row__team">
                          <span className="match-row__team-name">{row.strHomeTeam}</span>
                          {row.strHomeTeamBadge ? (
                            <img className="match-row__team-badge" loading="lazy" src={row.strHomeTeamBadge} alt={row.strHomeTeam || ''} />
                          ) : (
                            <div className="match-row__team-badge match-row__team-badge--emoji">
                              {SPORTS[selectedSportIdx].icon}
                            </div>
                          )}
                        </div>

                        <div className="match-row__center">
                          {isLive ? (
                            <>
                              <div className="match-row__score">
                                <span className="match-row__score-digit">{(row as LiveEvent).intHomeScore || '0'}</span>
                                <span className="match-row__score-sep">–</span>
                                <span className="match-row__score-digit">{(row as LiveEvent).intAwayScore || '0'}</span>
                              </div>
                              {(row as LiveEvent).strProgress && (
                                <span className="match-row__time">{(row as LiveEvent).strProgress}</span>
                              )}
                              <span className="match-row__status-live">
                                <span className="match-row__live-dot" />
                                {t('sports.ongoing')}
                              </span>
                              {row.strChannel && (
                                <div className="match-row__center-channel">
                                  {row.strChannelLogo ? (
                                    <img className="match-row__center-channel-logo" loading="lazy" src={row.strChannelLogo} alt={row.strChannel} />
                                  ) : null}
                                  <span className="match-row__center-channel-name">{row.strChannel}</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="match-row__vs">vs</span>
                              {(row as UpcomingEvent).strTime && (
                                <span className="match-row__time">{((row as UpcomingEvent).strTime || '').slice(0, 5)}</span>
                              )}
                              {dateLabelUpcoming && (
                                <span className="match-row__status-upcoming">{dateLabelUpcoming}</span>
                              )}
                            </>
                          )}
                        </div>

                        <div className="match-row__team match-row__team--away">
                          {row.strAwayTeamBadge ? (
                            <img className="match-row__team-badge" loading="lazy" src={row.strAwayTeamBadge} alt={row.strAwayTeam || ''} />
                          ) : (
                            <div className="match-row__team-badge match-row__team-badge--emoji">
                              {SPORTS[selectedSportIdx].icon}
                            </div>
                          )}
                          <span className="match-row__team-name">{row.strAwayTeam}</span>
                        </div>
                      </div>

                      {/* Bell + Channel (upcoming only) */}
                      <div className="match-row__right">
                        {row.kind === 'upcoming' && row.strChannel && (
                          <div className="match-row__channel">
                            {row.strChannelLogo ? (
                              <img className="match-row__channel-logo" loading="lazy" src={row.strChannelLogo} alt={row.strChannel} />
                            ) : null}
                            <span className="match-row__channel-name">{row.strChannel}</span>
                          </div>
                        )}
                        {row.kind === 'upcoming' && (
                          <div
                            className={[
                              'match-row__bell',
                              hasBell ? 'match-row__bell--on' : '',
                              isBellFocused ? 'match-row__bell--focused' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={function(ev) {
                              ev.stopPropagation();
                              setFocusSection('matches');
                              setMatchFocusedIdx(idx);
                              setBellFocused(true);
                              var sched = makeSchedule(row);
                              if (sched) toggleSchedule(sched);
                            }}
                          >
                            <FiBell />
                          </div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

        </div>{/* end body-area */}
      </div>

      {lockModalOpen && (
        <PasswordModal
          mode={lockModalMode}
          error={lockModalError ? t(lockModalError) : undefined}
          onConfirm={handleLockPasswordConfirm}
          onCancel={handleLockPasswordCancel}
        />
      )}
    </>
  );
};
